import axios from 'axios';
import {
    LOGIN_ATTEMPT,
    LOGIN_SUCCESS,
    LOGIN_FAILED,
    LOGOUT,
    RESET_SUCCESS,
    UPDATE_FAILED,
    UPDATE_SUCCESS,
    SESSION_BOOTSTRAP_DONE,
    SESSION_HYDRATE,
} from "../types/sessionTypes";
import { config } from "../../config";
import { sanitizeAuthUser, refreshSession, logoutSession } from '../../api/httpClient';
import { withAvatarCacheBust } from '../../utils/avatarUtils';
import { saveUserProfile, clearUserProfile, loadUserProfile } from '../../api/userSessionStorage';
import { getApiHeaders } from '../../api/apiHeaders';
import alertify from 'alertifyjs';
import 'alertifyjs/build/css/alertify.css';
import { mapPasswordApiError } from '../../utils/passwordPolicy';

const url_api = config.api.web;

export function loading() {
    return { type: LOGIN_ATTEMPT };
}

export function success(data) {
    const user = sanitizeAuthUser(data);
    saveUserProfile(user);
    return { type: LOGIN_SUCCESS, data: user };
}

export function failed(error) {
    return { type: LOGIN_FAILED, error: error };
}

export function signout() {
    clearUserProfile();
    return { type: LOGOUT };
}

export function successUpdate(success) {
    return { type: RESET_SUCCESS, success: success };
}

export function failedUpdate(error) {
    return { type: UPDATE_FAILED, error: error };
}

export function successItem(item) {
    const user = sanitizeAuthUser(item);
    saveUserProfile(user);
    return { type: UPDATE_SUCCESS, item: user };
}

export const logout = () => async (dispatch) => {
    // Clear local session first so Routes does not remount mid-login (e.g. email code flow).
    clearUserProfile();
    dispatch(signout());
    await logoutSession();
};

export const bootstrapSession = () => async (dispatch) => {
    const cached = loadUserProfile();

    if (cached && cached.id) {
        dispatch({
            type: SESSION_HYDRATE,
            data: sanitizeAuthUser(cached),
        });
    }

    try {
        const user = await refreshSession();
        dispatch(success(user));
    } catch (e) {
        clearUserProfile();
        dispatch(signout());
    } finally {
        dispatch({ type: SESSION_BOOTSTRAP_DONE });
    }
};

/** @deprecated use bootstrapSession */
export const restoreSession = bootstrapSession;

export const login = (data) => async (dispatch) => {
    dispatch(loading());
    try {
        const response = await axios.post(`${url_api}auth/login`, data);
        if (response.status < 300 && response.data.status === 'Success') {
            const body = response.data.body;
            if (body.requires2FA) {
                dispatch({ type: LOGIN_FAILED, error: '' }); // clears loading only
                return {
                    requires2FA: true,
                    method: body.method,
                    tempToken: body.tempToken,
                };
            }
            dispatch(success(body));
            return { success: true };
        }
        dispatch(failed(response.data.body || 'try_later'));
        return { success: false };
    } catch (error) {
        const message = error.response?.data?.body || 'try_later';
        dispatch(failed(message));
        return { success: false, error: message };
    }
};

export const verify2fa = (data) => async (dispatch) => {
    dispatch(loading());
    try {
        const response = await axios.post(`${url_api}auth/verify-2fa`, data);
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(success(response.data.body));
            return { success: true };
        }
        dispatch(failed(response.data.body || 'try_later'));
        return { success: false };
    } catch (error) {
        const message = error.response?.data?.body || 'try_later';
        dispatch(failed(message));
        return { success: false };
    }
};

export const sendLoginCode = (email) => async (dispatch) => {
    try {
        const response = await axios.post(`${url_api}auth/login-code/send`, { email });
        if (response.status < 300 && response.data.status === 'Success') {
            return { success: true, body: response.data.body };
        }
        const message = response.data.body || 'try_later';
        dispatch(failed(message));
        return { success: false, error: message };
    } catch (error) {
        const message = error.response?.data?.body || 'try_later';
        dispatch(failed(message));
        return { success: false, error: message };
    }
};

export const verifyLoginCode = (data) => async (dispatch) => {
    dispatch(loading());
    try {
        const response = await axios.post(`${url_api}auth/login-code/verify`, data);
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(success(response.data.body));
            return { success: true };
        }
        dispatch(failed(response.data.body || 'try_later'));
        return { success: false };
    } catch (error) {
        const message = error.response?.data?.body || 'try_later';
        dispatch(failed(message));
        return { success: false, error: message };
    }
};

export const resetItem = (data) => async (dispatch) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${url_api}auth/reset`,
            data: data.params,
            headers: getApiHeaders(),
        });
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(successUpdate(response.data.body));
        } else {
            dispatch(failedUpdate(response.data.body));
        }
    } catch (error) {
        dispatch(failedUpdate('try_later'));
    }
};

export const updateItem = (data) => async (dispatch) => {
    try {
        const response = await axios({
            method: 'put',
            url: `${url_api}auth/update`,
            data: data.params,
            headers: getApiHeaders(),
        });
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(successItem(response.data.body));
            alertify.success('Success');
        } else {
            dispatch(failedUpdate(response.data.body));
        }
    } catch (error) {
        dispatch(failedUpdate('try_later'));
    }
};

export const uploadAvatar = (file) => async (dispatch, getState) => {
    const form = new FormData();
    form.append('file', file);
    try {
        const response = await axios.post(`${url_api}auth/avatar`, form, {
            headers: getApiHeaders(),
            withCredentials: true,
        });
        if (response.status < 300 && response.data.status === 'Success') {
            const body = response.data.body || {};
            const avatar = body.avatar ? withAvatarCacheBust(body.avatar) : body.avatar;
            const currentAuth = getState()?.session?.auth || {};
            dispatch(successItem({ ...currentAuth, ...body, avatar }));
            alertify.success('Avatar updated');
            return { success: true, avatar };
        }
        dispatch(failedUpdate(response.data.body || 'try_later'));
        alertify.error('Could not update avatar');
        return { success: false };
    } catch (error) {
        const message = error.response?.data?.body || 'try_later';
        dispatch(failedUpdate(message));
        alertify.error('Could not update avatar');
        return { success: false };
    }
};

export const load2faStatus = () => async () => {
    const response = await axios.get(`${url_api}auth/2fa/status`, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const cancel2faSetup = () => async () => {
    const response = await axios.post(`${url_api}auth/2fa/cancel-setup`, {}, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const setup2faEmail = () => async () => {
    const response = await axios.post(`${url_api}auth/2fa/setup-email`, {}, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        alertify.success(response.data.body?.message || 'Code sent');
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const setup2faTotp = () => async () => {
    const response = await axios.post(`${url_api}auth/2fa/setup-totp`, {}, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const enable2fa = (code) => async () => {
    const response = await axios.post(`${url_api}auth/2fa/enable`, { code }, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        alertify.success(response.data.body?.message || '2FA enabled');
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const disable2fa = (password) => async () => {
    const response = await axios.post(`${url_api}auth/2fa/disable`, { password }, {
        headers: getApiHeaders(),
        withCredentials: true,
    });
    if (response.status < 300 && response.data.status === 'Success') {
        alertify.success(response.data.body?.message || '2FA disabled');
        return response.data.body;
    }
    throw new Error(response.data.body || 'try_later');
};

export const deleteAccount = ({ password, confirmation }) => async (dispatch) => {
    const response = await axios.post(
        `${url_api}auth/delete-account`,
        { password, confirmation },
        {
            headers: getApiHeaders(),
            withCredentials: true,
        },
    );
    if (response.status < 300 && response.data.status === 'Success') {
        await logoutSession();
        dispatch(signout());
        return { success: true };
    }
    throw new Error(response.data.body || 'try_later');
};

export const loadItem = (data) => async (dispatch) => {
    dispatch(loading());
    try {
        const response = await axios.get(`${url_api}auth/profile`, {
            params: { id: data.user_id },
            headers: getApiHeaders(),
        });
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(successItem(response.data.body));
        } else {
            dispatch(failed(response.data.body));
        }
    } catch (error) {
        dispatch(failed('try_later'));
    }
};

export const activate = (data) => async (dispatch) => {
    try {
        const response = await axios.post(`${url_api}auth/activate`, data.params);
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(successUpdate(response.data.body));
        } else {
            dispatch(failedUpdate(response.data.body));
        }
    } catch (error) {
        dispatch(failedUpdate('try_later'));
    }
};

export const register = (data) => async (dispatch) => {
    try {
        const { policy, ...payload } = data.params || {};
        const response = await axios.post(`${url_api}auth/register`, payload);
        if (response.status < 300 && response.data.status === 'Success') {
            const body = response.data.body || {};
            const messageKey = body.requiresConfirmation ? 'register_confirm_email' : 'register_success';
            dispatch(successUpdate(messageKey));
            return { success: true, requiresConfirmation: !!body.requiresConfirmation };
        }
        const message = mapPasswordApiError(response.data.body);
        dispatch(failedUpdate(message));
        return { success: false };
    } catch (error) {
        const message = mapPasswordApiError(error.response?.data?.body);
        dispatch(failedUpdate(message));
        return { success: false };
    }
};

export const confirmEmail = (token) => async () => {
    const response = await axios.post(`${url_api}auth/confirm-email`, { token });
    if (response.status < 300 && response.data.status === 'Success') {
        return response.data.body || {};
    }
    throw new Error(response.data.body || 'try_later');
};

export const recovery = (data) => async (dispatch) => {
    try {
        const response = await axios.post(`${url_api}email/recovery`, data);
        if (response.status < 300 && response.data.status === 'Success') {
            dispatch(successUpdate(response.data.body));
        } else {
            dispatch(failedUpdate(response.data.body));
        }
    } catch (error) {
        dispatch(failedUpdate('try_later'));
    }
};
